<template>
  <div class="talent-card-btn-wrapper">
    <!-- Trigger Button -->
    <vs-tooltip :text="$t('orgDashboard.talentPool.view_talent_card')" v-if="userId && !hideTrigger">
      <vs-button
        class="action-btn"
        color="success"
        type="filled"
        icon-pack="feather"
        icon="icon-user"
        size="small"
        :disabled="isLoading"
        @click.stop="openTalentCard"
      >
      </vs-button>
    </vs-tooltip>

    <!-- Popup -->
    <vs-popup class="talent-card-popup" title="" :active.sync="isPopupActive">
      
      <!-- Custom Close Icon (since we hide default header) -->
      <div class="card-close-icon" @click="isPopupActive = false">
        <feather-icon icon="XIcon" class="w-6 h-6 cursor-pointer" />
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="con-vs-loading"><div class="vs-loading"></div></div>
        <p>Fetching Profile...</p>
      </div>

      <!-- Content -->
      <div v-else-if="profileData" class="card-container">
        <!-- Header Section -->
        <div class="card-header">
          <div class="avatar-wrapper">
             <!-- Match Main View Avatar Logic -->
             <vs-avatar 
               v-if="profileData.profilePictureUrl"
               size="100px"
               class="tc-avatar-component"
               :src="`https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${profileData.profilePictureUrl}`" 
             />
             <vs-avatar 
               v-else 
               size="100px"
               class="tc-avatar-component"
               :src="`https://hostedboringavatars.vercel.app/api/beam?name=${profileData.firstName}+${profileData.lastName}&colors=F2C244,E77E23,7367F0,28C76F,EA5455`" 
             />
          </div>
        </div>

        <!-- Card Body -->
        <div class="card-body">
          <h2 class="user-name">{{ profileData.firstName }} {{ profileData.lastName }}</h2>
          <p class="user-role">{{ profileData.profession || 'Candidate' }}</p>

          <div class="divider"></div>

          <!-- CAREER OBJECTIVE -->
          <div class="info-section">
            <h5 class="section-label">{{ $t('orgDashboard.talentPool.career_objective') }}</h5>
            <p class="objective-text">
              {{ profileData.careerObjective || $t('orgDashboard.talentPool.no_objective_provided') }}
            </p>
          </div>

          <!-- CONTACT INFO -->
          <div class="info-section contact-info">
            <h5 class="section-label">{{ $t('orgDashboard.talentPool.contact_info') }}</h5>

            <div class="contact-row" v-if="profileData.address">
              <feather-icon icon="MapPinIcon" class="icon-small" />
              <span>{{ profileData.address }}</span>
            </div>

            <!-- PHONE & WHATSAPP / TELEGRAM ICONS (Right next to number) -->
            <div class="contact-row" v-if="profileData.contactNumber">
              <feather-icon icon="PhoneIcon" class="icon-small" />
              <span style="margin-right: 12px;">{{ profileData.contactNumber }}</span>
              
              <div class="social-actions">
                <!-- WhatsApp Icon -->
                <a :href="getWhatsAppLink(profileData.contactNumber)" target="_blank" title="Message on WhatsApp" class="social-icon whatsapp">
                  <svg style="width:18px;height:18px;" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                  </svg>
                </a>
                <!-- Telegram Icon -->
                <a :href="getTelegramLink(profileData.contactNumber)" target="_blank" title="Message on Telegram" class="social-icon telegram">
                  <svg style="width:18px;height:18px;" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.504 8.527l-1.854 8.74c-.139.63-.513.784-1.04.488l-2.875-2.119-1.388 1.336c-.153.153-.282.282-.578.282l.207-2.913 5.305-4.791c.231-.205-.05-.319-.358-.113l-6.557 4.126-2.825-.883c-.614-.192-.627-.614.128-.909l11.04-4.254c.511-.192.964.116.804.91z"/>
                  </svg>
                </a>
              </div>
            </div>

            <!-- EMAIL LINK -->
            <div class="contact-row">
              <feather-icon icon="MailIcon" class="icon-small" />
              <a :href="'mailto:' + (profileData.email || email)" class="email-link" title="Send Email">
                {{ profileData.email || email }}
              </a>
            </div>
          </div>

          <!-- SKILLS -->
          <div class="info-section" v-if="formattedSkills.length > 0">
            <h5 class="section-label">{{ $t('orgDashboard.talentPool.skills') }}</h5>
            <div class="skills-container">
              <span v-for="(skill, i) in formattedSkills" :key="i" class="skill-chip">
                {{ skill }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
const API_BASE_URL = "https://api.getboarded.tech";

export default {
  name: 'TalentCardPreview',
  props: {
    userId: { type: String, default: '' },
    email: { type: String, default: '' },
    hideTrigger: { type: Boolean, default: false } 
  },
  data() {
    return {
      isPopupActive: false,
      isLoading: false,
      profileData: null,
    };
  },
  computed: {
    formattedSkills() {
      if (!this.profileData) return [];
      if (this.profileData.skills && this.profileData.skills.length) {
         return this.profileData.skills.map(s => s.title || s);
      }
      if (this.profileData.extraCurricular && this.profileData.extraCurricular.length > 0) {
        const ec = this.profileData.extraCurricular[0];
        if (ec.skills && Array.isArray(ec.skills)) {
          return ec.skills.map(s => s.title || s);
        }
      }
      return [];
    }
  },
  watch: {
    userId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.profileData = null; // Clear old data to show loader for the new user
      }
    }
  },
  methods: {
    getWhatsAppLink(phone) {
      if (!phone) return '#';
      // Strip everything except digits
      const cleaned = phone.toString().replace(/\D/g, '');
      return `https://wa.me/${cleaned}`;
    },
    getTelegramLink(phone) {
      if (!phone) return '#';
      // Remove spaces, hyphens, parentheses
      const cleaned = phone.toString().replace(/[\s\-\(\)]/g, '');
      // Make sure it starts with + for telegram
      const formatted = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
      return `https://t.me/${formatted}`;
    },
    async openTalentCard() {
      if (!this.userId) return;
      this.isPopupActive = true;
      if (this.profileData) return;

      this.isLoading = true;
      try {
        const url = `${API_BASE_URL}/users/${this.userId}`;
        const token = localStorage.getItem('accessToken') || this.$store.state.bearerToken;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) throw new Error('API Error');
        this.profileData = await response.json();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.talent-card-btn-wrapper {
  display: inline-block;
}

/* Button Styling */
.action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
}

.action-btn >>> .vs-icon {
  color: #28c76f !important;
  font-size: 16px !important;
  margin: 0 !important;
}

.action-btn:hover {
  background: #28c76f !important;
  border-radius: none !important;
}

.action-btn:hover >>> .vs-icon {
  color: #ffffff !important;
}

.talent-card-popup >>> .vs-popup {
  width: 400px !important;
  background: transparent !important;
  box-shadow: none !important;
}

.talent-card-popup >>> .vs-popup--header {
  display: none !important; /* Remove the white bar header */
}

.talent-card-popup >>> .vs-popup--content {
  padding: 0 !important;
  border-radius: 20px;
  overflow: hidden;
  background: white;
  position: relative;
  max-height: 80vh;           
  overflow-y: auto;           
  scrollbar-width: none;      
  -ms-overflow-style: none;  
}

.talent-card-popup >>> .vs-popup--content::-webkit-scrollbar {
  display: none;
}

.card-close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 100;
  color: #626262;
  background: rgba(255,255,255,0.4);
  border-radius: 50%;
  padding: 4px;
}

/* CARD DESIGN */
.card-container {
  display: flex;
  flex-direction: column;
}

.card-header {
  height: 100px;
  background: #fdfdfd;
  display: flex;
  justify-content: center;
  position: relative;
}

.avatar-wrapper {
  position: absolute;
  bottom: -50px;
  z-index: 10;
}

.tc-avatar-component {
  border: 5px solid white !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.card-body {
  background: linear-gradient(135deg, #7367F0 0%, #9E95F5 100%);
  color: white;
  padding: 65px 30px 40px 30px;
  text-align: left;
}

.user-name {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.user-role {
  text-align: center;
  font-size: 1rem;
  opacity: 0.9;
  margin-top: 4px;
  margin-bottom: 25px;
}

.divider {
  height: 1px;
  background: rgba(255,255,255,0.25);
  margin-bottom: 25px;
}

.info-section {
  margin-bottom: 25px;
}

.section-label {
  font-size: 0.75rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  font-weight: 600;
  margin-bottom: 10px;
}

.objective-text {
  font-size: 0.9rem;
  line-height: 1.6;
}

.contact-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.social-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.social-icon {
  color: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.social-icon:hover {
  transform: scale(1.15);
}

.social-icon.whatsapp:hover {
  color: #25D366; /* WhatsApp Brand Color */
}

.social-icon.telegram:hover {
  color: #229ED9; /* Telegram Brand Color */
}

.email-link {
  color: white;
  text-decoration: none;
  transition: opacity 0.2s;
}

.email-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.icon-small {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  opacity: 0.8;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-chip {
  background: rgba(255,255,255,0.25);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
}

.loading-state {
  padding: 60px;
  text-align: center;
  color: #7367F0;
}
</style>
